import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { InquiryType, inquiryReasons } from '../../data/contact';
import { ContactFields, useContactForm } from './useContactForm';
import { easeOutStrong } from '../../utils/motion';
type ContactFormProps = {
  presetInquiry: InquiryType;
};
const inputBase = 'w-full bg-white px-4 py-3 border rounded-md text-ink-dark placeholder:text-ink-muted/70 focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-colors duration-150';
export function ContactForm({
  presetInquiry
}: ContactFormProps) {
  const {
    fields,
    errors,
    status,
    update,
    submit,
    reset
  } = useContactForm();
  useEffect(() => {
    if (presetInquiry) update('inquiryType', presetInquiry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetInquiry]);
  const fieldProps = (key: keyof ContactFields) => ({
    id: key,
    name: key,
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? `${key}-error` : undefined,
    className: `${inputBase} ${errors[key] ? 'border-red-600' : 'border-line'}`
  });
  return <div className="bg-white border border-line rounded-lg p-6 sm:p-8 lg:p-10" data-id="element-310" data-mp="f37a5ysnqbnw">
      <AnimatePresence mode="wait" initial={false} data-id="element-311" data-mp="f14n9nmswyne">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: easeOutStrong
      }} role="status" className="py-10 text-center" data-id="element-312" data-mp="b3gm42uim9n2">
            <CheckCircle2 size={40} className="mx-auto text-teal mb-5" aria-hidden="true" data-id="element-313" data-mp="yt14ajiuh2va" />
            <h2 className="font-heading font-bold text-2xl text-ink-dark mb-3" data-id="element-314" data-mp="tug5moj8qr8y">Thanks, {fields.firstName}.</h2>
            <p className="text-ink-muted leading-relaxed max-w-sm mx-auto mb-8" data-id="element-315" data-mp="3eicapde1bcpf">
              Your message is on its way. We typically respond within 2–3
              business days at {fields.email}.
            </p>
            <button type="button" onClick={reset} className="text-navy font-semibold hover:text-navy-light transition-colors duration-150" data-id="element-316" data-mp="3kz4f206w6fx9">
              Send another message
            </button>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} onSubmit={submit} noValidate className="space-y-5" data-id="element-317" data-mp="2x9a2ljqhitdj">
            <h2 className="font-heading font-bold text-2xl text-ink-dark" data-id="element-318" data-mp="2s9x76aljsdzc">Send us a message</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-319" data-mp="2ea5tkayy0zlv">
              <Field label="First name" htmlFor="firstName" required error={errors.firstName} data-id="element-320" data-mp="2eaxpjmwgczco">
                <input type="text" autoComplete="given-name" value={fields.firstName} onChange={e => update('firstName', e.target.value)} {...fieldProps('firstName')} data-id="element-321" data-mp="2j8fpwowem692" />
              </Field>
              <Field label="Last name" htmlFor="lastName" required error={errors.lastName} data-id="element-322" data-mp="28pgfreqnieor">
                <input type="text" autoComplete="family-name" value={fields.lastName} onChange={e => update('lastName', e.target.value)} {...fieldProps('lastName')} data-id="element-323" data-mp="24kyzvqnewlj9" />
              </Field>
            </div>

            <Field label="Email address" htmlFor="email" required error={errors.email} data-id="element-324" data-mp="2se7bl6h6s167">
              <input type="email" autoComplete="email" value={fields.email} onChange={e => update('email', e.target.value)} {...fieldProps('email')} data-id="element-325" data-mp="2ndte2id7o9mf" />
            </Field>

            <Field label="Organization" htmlFor="organization" data-id="element-326" data-mp="unhbl74zvdug">
              <input type="text" autoComplete="organization" value={fields.organization} onChange={e => update('organization', e.target.value)} {...fieldProps('organization')} data-id="element-327" data-mp="znn74yedxkht" />
            </Field>

            <Field label="What is this about?" htmlFor="inquiryType" required error={errors.inquiryType} data-id="element-328" data-mp="bpo8a3qgl1wj">
              <select value={fields.inquiryType} onChange={e => update('inquiryType', e.target.value as InquiryType)} {...fieldProps('inquiryType')} data-id="element-329" data-mp="gorazhiyuw1c">
                <option value="" data-id="element-330" data-mp="ppz29fn2ewlx">Select an option</option>
                {inquiryReasons.map(reason => <option key={reason.value} value={reason.value} data-id="element-331" data-mp="1w6b0fgpw0ehi">
                    {reason.option}
                  </option>)}
              </select>
            </Field>

            <Field label="Message" htmlFor="message" required error={errors.message} data-id="element-332" data-mp="2b92t8toykm7r">
              <textarea rows={5} placeholder="Tell us about your organization and what you'd like to explore." value={fields.message} onChange={e => update('message', e.target.value)} {...fieldProps('message')} className={`${fieldProps('message').className} resize-y`} data-id="element-333" data-mp="269q6sa201h5p" />
            </Field>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2" data-id="element-334" data-mp="1nifg9u06r6ec">
              <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light disabled:opacity-70 disabled:cursor-wait text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-335" data-mp="1ijvtxshojslg">
                {status === 'submitting' && <Loader2 size={18} className="animate-spin" aria-hidden="true" data-id="element-336" data-mp="38ugxbkcwclaf" />}
                {status === 'submitting' ? 'Sending…' : 'Send Message'}
              </button>
              <p className="text-sm text-ink-muted" data-id="element-337" data-mp="1b1nv82ma2edl">All fields except Organization are required.</p>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
type FieldProps = {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
};
function Field({
  label,
  htmlFor,
  required,
  error,
  children
}: FieldProps) {
  return <div data-id="element-338" data-mp="1tyfmozbz3zsq">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-dark mb-2" data-id="element-339" data-mp="1tx4iiyem8q13">
        {label}
        {required && <span className="sr-only" data-id="element-340" data-mp="1kyisyjaq8aqt"> (required)</span>}
      </label>
      {children}
      {error && <p id={`${htmlFor}-error`} className="mt-1.5 text-sm text-red-700" data-id="element-341" data-mp="4kai3lzbjpv0">
          {error}
        </p>}
    </div>;
}